import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { usePropertyField, useProperty } from '@vibesites/runtime/react';

/**
 * Weidner Via6 NeighborhoodMap.
 *
 * Mirrors the rise theme's NeighborhoodMap behavior, markers, and Google
 * Map styling verbatim — the divergences from the rise base are
 * CHROME-ONLY, modeled after the editorial layout used on
 * https://www.via6living.com/neighborhood :
 *
 *   - Category tabs render as Via6-style uppercase, letter-spaced text
 *     links with a thin underline on the active tab (no pill button
 *     group, no joined-corner borders).
 *   - The POI list sits to the LEFT of the map (sidebar feel), with the
 *     map taking the larger right column on desktop.
 *   - POI items are minimal numbered rows: a small numbered circle, the
 *     POI name in the heading serif, address and distance below — no
 *     image thumbnail, no card background. Items are separated by a
 *     hairline divider rather than a bordered card.
 *   - Each accordion section header carries a small inline category
 *     glyph (reusing the same `CATEGORY_ICONS` set used for map
 *     markers, so the sidebar and the markers share a visual
 *     vocabulary) and a thin underline beneath the title text.
 *
 * The underlying Google Map styles, property pin, category markers, and
 * detail-panel content are untouched on purpose — only the surrounding
 * tab + sidenav chrome matches the Via6 reference.
 */

interface POI {
  poi_name?: string;
  poi_type?: string;
  poi_address?: string;
  poi_short_description?: string;
  poi_long_description?: string;
  poi_featured_image?: string;
  poi_business_url?: string;
  poi_business_phone?: string;
  poi_directions_map_url?: string;
  poi_miles_from_property?: string;
  poi_walking_minutes?: string;
  poi_driving_minutes?: string;
  poi_biking_minutes?: string;
  poi_latitude?: string;
  poi_longitude?: string;
  poi_map_icon?: string;
  poi_google_place_id?: string;
  poi_place_primary_type?: string;
}

function hasVal(v: string | null | undefined): v is string {
  return !!v && v !== 'false' && v !== 'null' && v !== 'undefined' && v.trim() !== '';
}

function formatMinutes(raw: string, mode: string): string {
  const trimmed = raw.trim();
  const hasMin = /\bmin/i.test(trimmed);
  const hasMode = new RegExp(`\\b${mode}\\b`, 'i').test(trimmed);
  // Normalize so the output always ends with `<number> min(s) <mode>`,
  // even when the source data already includes only the "min" half
  // (e.g. "5 mins") or only the digit. This way the sidebar shows
  // "5 min walk" consistently regardless of how editors typed the
  // raw value.
  let result = trimmed;
  if (!hasMin) result = `${result} min`;
  if (!hasMode) result = `${result} ${mode}`;
  return result;
}

// ============================================================================
// Map styling & markers
// ============================================================================

const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.GOOGLE_MAPS_API_KEY) || '';

let mapsLoaderPromise: Promise<void> | null = null;

function loadGoogleMaps(): Promise<void> {
  if ((window as any).google?.maps?.Map) return Promise.resolve();
  if (mapsLoaderPromise) return mapsLoaderPromise;
  mapsLoaderPromise = new Promise<void>((resolve, reject) => {
    if (!MAPS_API_KEY) { reject(new Error('No key')); return; }
    const cb = `__gmapsInit_${Date.now()}`;
    (window as any)[cb] = () => { delete (window as any)[cb]; resolve(); };
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&callback=${cb}`;
    s.onerror = () => { mapsLoaderPromise = null; reject(new Error('Script failed')); };
    document.head.appendChild(s);
  });
  return mapsLoaderPromise;
}

function makeSvgMarker(fillColor: string, glyphSvg: string, size = 30): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${Math.round(size * 1.3)}" viewBox="0 0 30 39">
  <path d="M15 0C6.72 0 0 6.72 0 15c0 11.25 15 24 15 24s15-12.75 15-24C30 6.72 23.28 0 15 0z" fill="${fillColor}" stroke="#fff" stroke-width="1.5"/>
  <g transform="translate(7,7)" fill="#fff"><g transform="translate(8,8) scale(0.55) translate(-8,-8)">${glyphSvg}</g></g>
</svg>`)}`;
}

function makeHighlightMarker(fillColor: string, glyphSvg: string, size = 40): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${Math.round(size * 1.35)}" viewBox="0 0 40 54">
  <ellipse cx="20" cy="51" rx="8" ry="3" fill="rgba(0,0,0,0.2)"/>
  <path d="M20 0C9 0 0 9 0 20c0 15 20 31 20 31s20-16 20-31C40 9 31 0 20 0z" fill="${fillColor}" stroke="#fff" stroke-width="2"/>
  <g transform="translate(10,10)" fill="#fff"><g transform="translate(10,10) scale(0.7) translate(-8,-8)">${glyphSvg}</g></g>
</svg>`)}`;
}

function makePropertyPinBg(primaryColor: string, hasIcon: boolean): string {
  const inner = hasIcon
    ? `<circle cx="30" cy="27" r="18" fill="#fff"/>`
    : `<circle cx="30" cy="27" r="18" fill="#fff" opacity="0.95"/>
       <path d="M24 26v10h4v-5h4v5h4V26l-6-5z" fill="${primaryColor}"/>
       <path d="M21 27l9-8 9 8" fill="none" stroke="${primaryColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="60" height="74" viewBox="0 0 60 74">
  <ellipse cx="30" cy="70" rx="12" ry="4" fill="rgba(0,0,0,0.2)"/>
  <path d="M30 0C13.43 0 0 13.43 0 30c0 22.5 30 42 30 42s30-19.5 30-42C60 13.43 46.57 0 30 0z" fill="${primaryColor}" stroke="#fff" stroke-width="2.5"/>
  ${inner}
</svg>`)}`;
}

const CATEGORY_ICONS: Record<string, { glyph: string; hue: number }> = {
  restaurant: { glyph: '<path d="M2 0v6h2v10h2V6h2V0H6V4H4V0zm8 0v8h4V6h2v10h2V0z" transform="scale(0.9)"/>', hue: 0 },
  shopping: { glyph: '<path d="M2 2v2H0v10h16V4h-2V2a4 4 0 00-8 0v0zm2 0a2 2 0 014 0v2H4V2z" transform="scale(0.95)"/>', hue: 270 },
  education: { glyph: '<path d="M8 0L0 4l8 4 8-4-8-4zM2 6v4l6 3 6-3V6L8 9 2 6z"/>', hue: 200 },
  health: { glyph: '<path d="M6 0v6H0v4h6v6h4v-6h6V6h-6V0z" transform="scale(0.95)"/>', hue: 350 },
  fitness: { glyph: '<path d="M6 0v6H0v4h6v6h4v-6h6V6h-6V0z" transform="scale(0.95)"/>', hue: 350 },
  entertainment: { glyph: '<path d="M8 0a8 8 0 100 16A8 8 0 008 0zm0 2a6 6 0 110 12A6 6 0 018 2zm-2 3v6l5-3-5-3z" transform="scale(0.95)"/>', hue: 45 },
  park: { glyph: '<path d="M8 0L4 6h2L2 12h12L10 6h2L8 0zM7 12v4h2v-4z" transform="scale(0.95)"/>', hue: 140 },
  // Nature shares the park (tree) glyph and forest hue — a "Nature"
  // category on the editor side reads the same as parks/greenspace on
  // the map.
  nature: { glyph: '<path d="M8 0L4 6h2L2 12h12L10 6h2L8 0zM7 12v4h2v-4z" transform="scale(0.95)"/>', hue: 140 },
  transit: { glyph: '<path d="M4 0C2 0 0 1 0 3v7a3 3 0 003 3l-1 2v1h2l1-2h6l1 2h2v-1l-1-2a3 3 0 003-3V3c0-2-2-3-4-3zm0 2h8v4H4V2zm0 7a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm8 0a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" transform="scale(0.95)"/>', hue: 210 },
  grocery: { glyph: '<path d="M0 0v2h2l3 6.5L3.5 11A2 2 0 005.5 13h9v-2h-9l1-2h6a1 1 0 00.9-.5L15.5 4H4L3 2H0zm5.5 14a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm7 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" transform="scale(0.95)"/>', hue: 120 },
  // Major employers / offices / business districts — a small
  // skyscraper outline with three columns of windows. Slate-grey hue
  // so the markers read as "corporate" rather than competing with the
  // brand color used for shopping/dining.
  employer: { glyph: '<path d="M2 1v14h12V1H2zm2 2h2v2H4V3zm4 0h2v2H8V3zm-4 4h2v2H4V7zm4 0h2v2H8V7zm-4 4h2v2H4v-2zm4 0h2v2H8v-2z" transform="scale(0.95)"/>', hue: 220 },
  default: { glyph: '<circle cx="8" cy="8" r="4" fill="#fff"/>', hue: 0 },
};

function getCategoryStyle(poiType: string, primaryHue: number): { glyph: string; hue: number } {
  const lower = (poiType || '').toLowerCase();
  for (const [key, val] of Object.entries(CATEGORY_ICONS)) {
    if (key === 'default') continue;
    if (lower.includes(key)) return val;
  }
  return { ...CATEGORY_ICONS.default, hue: (primaryHue + 180) % 360 };
}

/**
 * Larger, cleaner icon set for the accordion section headers.
 *
 * Modeled on the editorial sidebar at via6living.com/neighborhood —
 * filled white pictograms (fork+knife, cart, grad cap, music note,
 * medical cross, etc.) drawn at viewBox 0 0 24 24, so they read
 * crisply at the ~22px size used in the header bar.
 *
 * Intentionally kept SEPARATE from the `CATEGORY_ICONS` map used to
 * paint the Google Map markers — those glyphs were tuned for small
 * 16-30px pin renders, and substituting these heavier 24x24 paths
 * into the markers would degrade legibility at marker scale.
 */
const SIDEBAR_CATEGORY_ICONS: Record<string, string> = {
  // Crossed fork & knife (Material "restaurant").
  restaurant: '<path d="M8.1 13.34l2.83-2.83L3.91 3.5c-1.56 1.56-1.56 4.09 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.2-1.1-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>',
  // Shopping cart with wheels (Material "shopping_cart").
  shopping: '<path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>',
  // Graduation cap / mortarboard (Material "school").
  education: '<path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>',
  // Single eighth note (Material "music_note").
  entertainment: '<path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>',
  // Bold medical cross / plus.
  health: '<path d="M11 3h2v8h8v2h-8v8h-2v-8H3v-2h8z"/>',
  wellness: '<path d="M11 3h2v8h8v2h-8v8h-2v-8H3v-2h8z"/>',
  fitness: '<path d="M11 3h2v8h8v2h-8v8h-2v-8H3v-2h8z"/>',
  // Tree (Material "park") — shared by both "park" and "nature"
  // category names. "Nature" categories typically cover greenspace,
  // trails, and parks together, so the same pictogram serves both.
  park: '<path d="M17 12h2L12 2 5.05 12H7l-3.9 6h6.92v3h3.95v-3H21z"/>',
  nature: '<path d="M17 12h2L12 2 5.05 12H7l-3.9 6h6.92v3h3.95v-3H21z"/>',
  // Bus (Material "directions_bus").
  transit: '<path d="M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4s-8 .5-8 4v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6H6V6h12v5z"/>',
  // Grocery → same cart glyph as shopping.
  grocery: '<path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>',
  // Major employers / offices / business districts — Material
  // "business" skyscraper with two roofline tiers and column-grouped
  // windows. Matches the via6living.com employer-card visual where
  // corporate POIs read as office towers rather than generic pins.
  employer: '<path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>',
  // Fallback dot — never seen in practice unless a property uses a
  // totally novel poi_type string.
  default: '<circle cx="12" cy="12" r="3"/>',
};

function getSidebarCategoryGlyph(poiType: string): string {
  const lower = (poiType || '').toLowerCase();
  for (const [key, glyph] of Object.entries(SIDEBAR_CATEGORY_ICONS)) {
    if (key === 'default') continue;
    if (lower.includes(key)) return glyph;
  }
  return SIDEBAR_CATEGORY_ICONS.default;
}

/**
 * Inline glyph for an accordion section header. Uses the editorial
 * `SIDEBAR_CATEGORY_ICONS` set (matches via6living.com/neighborhood),
 * NOT the marker glyph set. Inherits `currentColor` from the header
 * bar text so it stays white-on-primary like the rest of the header
 * chrome.
 */
function CategoryIcon({ poiType }: { poiType: string }) {
  const glyph = getSidebarCategoryGlyph(poiType);
  return (
    <svg
      className="nb-section-cat-icon"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <g dangerouslySetInnerHTML={{ __html: glyph }} />
    </svg>
  );
}

function hueToHex(hue: number, sat = 45, light = 35): string {
  const h = hue / 360, s = sat / 100, l = light / 100;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1/6) return p + (q-p)*6*t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q-p)*(2/3-t)*6;
    return p;
  };
  const q = l < 0.5 ? l*(1+s) : l+s-l*s;
  const p = 2*l - q;
  const r = Math.round(hue2rgb(p,q,h+1/3)*255);
  const g = Math.round(hue2rgb(p,q,h)*255);
  const b = Math.round(hue2rgb(p,q,h-1/3)*255);
  return `#${((1<<24)+(r<<16)+(g<<8)+b).toString(16).slice(1)}`;
}

function hexToHue(hex: string): number {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return 200;
  const r = parseInt(m[1],16)/255, g = parseInt(m[2],16)/255, b = parseInt(m[3],16)/255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b);
  if (max === min) return 0;
  const d = max-min;
  let h = 0;
  if (max === r) h = ((g-b)/d + (g<b?6:0))/6;
  else if (max === g) h = ((b-r)/d+2)/6;
  else h = ((r-g)/d+4)/6;
  return Math.round(h*360);
}

/**
 * Decompose a `#rrggbb` color into HSL channels. Counterpart to
 * `hueToHex` so we can round-trip through HSL space — needed for the
 * `lighten` rule below, which has to preserve the original hue and
 * saturation while bumping only the lightness channel.
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return { h: 200, s: 50, l: 50 };
  const r = parseInt(m[1], 16) / 255;
  const g = parseInt(m[2], 16) / 255;
  const b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Produce a lighter shade of a `#rrggbb` color by raising its HSL
 * lightness by `amount` percentage points (clamped to 100). Hue and
 * saturation are preserved so the result still reads as "the same
 * color, just paler" — useful for derived chrome tints (e.g. the
 * accordion background, which wants to sit one tier lighter than the
 * surrounding primary-color section so the accordion reads as a
 * distinct surface).
 */
function lighten(hex: string, amount: number): string {
  const { h, s, l } = hexToHsl(hex);
  const nextL = Math.min(100, Math.max(0, l + amount));
  return hueToHex(h, s, nextL);
}

// Map styling kept identical to the rise base — only the surrounding
// tabs and POI sidenav are themed for Via6. The map's brand-tinted water
// derives from `property_primary_color` so it continues to feel on-brand
// per property.
function buildMapStyles(primaryColor: string) {
  const hue = hexToHue(primaryColor);
  return [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
    { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: hueToHex(hue, 15, 85) }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  ];
}

// ============================================================================
// Google Map component
// ============================================================================

function GoogleMap({
  propertyLat, propertyLng, propertyIcon, primaryColor, pois, activeTab,
  selectedPoiIndex, onMarkerClick,
}: {
  propertyLat: number;
  propertyLng: number;
  propertyIcon: string;
  primaryColor: string;
  pois: POI[];
  activeTab: string;
  selectedPoiIndex: number | null;
  onMarkerClick: (index: number) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<{ marker: any; normalIcon: any; highlightIcon: any; poiIndex: number }[]>([]);
  const infoWindowRef = useRef<any>(null);
  const [ready, setReady] = useState(false);
  const primaryHue = useMemo(() => hexToHue(primaryColor), [primaryColor]);
  // Tracks the POI count we last fit the camera to. fitBounds should
  // only run when the POI set genuinely changes shape (initial load,
  // editor adds/removes a POI) — NOT when the parent re-renders for
  // unrelated reasons like selection state. Without this guard the
  // user sees a brief zoom-out every time they click a POI, because
  // the parent re-render churns reference identity of `pois` and
  // `onMarkerClick`, which churns `syncMarkers`, which re-fires this
  // effect.
  const lastFitPoiCountRef = useRef<number>(-1);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then(() => {
      if (cancelled || !containerRef.current) return;
      const g = (window as any).google.maps;
      const map = new g.Map(containerRef.current, {
        center: { lat: propertyLat, lng: propertyLng },
        zoom: 14,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: { position: g.ControlPosition.RIGHT_BOTTOM },
        styles: buildMapStyles(primaryColor),
      });

      infoWindowRef.current = new g.InfoWindow();

      const hasIcon = !!propertyIcon;
      new g.Marker({
        position: { lat: propertyLat, lng: propertyLng },
        map, title: 'Property', zIndex: 999,
        icon: {
          url: makePropertyPinBg(primaryColor, hasIcon),
          scaledSize: new g.Size(60, 74),
          anchor: new g.Point(30, 74),
        },
      });
      if (hasIcon) {
        new g.Marker({
          position: { lat: propertyLat, lng: propertyLng },
          map, zIndex: 1000, clickable: false,
          icon: {
            url: propertyIcon,
            scaledSize: new g.Size(24, 24),
            anchor: new g.Point(12, 59),
          },
        });
      }
      mapRef.current = map;
      setReady(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [propertyLat, propertyLng, primaryColor, propertyIcon]);

  const syncMarkers = useCallback(() => {
    const map = mapRef.current;
    const g = (window as any).google?.maps;
    if (!map || !g) return;

    markersRef.current.forEach((m) => m.marker.setMap(null));
    markersRef.current = [];

    const visible = activeTab === 'all'
      ? pois.map((p, i) => ({ poi: p, origIndex: i }))
      : pois.map((p, i) => ({ poi: p, origIndex: i })).filter(({ poi }) => (poi.poi_type || 'Nearby') === activeTab);

    const bounds = new g.LatLngBounds();
    bounds.extend({ lat: propertyLat, lng: propertyLng });
    let hasPoiBounds = false;
    const iconCache = new Map<string, { normal: any; highlight: any }>();

    for (const { poi, origIndex } of visible) {
      const lat = parseFloat(poi.poi_latitude || '');
      const lng = parseFloat(poi.poi_longitude || '');
      if (isNaN(lat) || isNaN(lng)) continue;

      const pos = { lat, lng };
      bounds.extend(pos);
      hasPoiBounds = true;

      const cat = poi.poi_type || 'Nearby';
      if (!iconCache.has(cat)) {
        const style = getCategoryStyle(cat, primaryHue);
        iconCache.set(cat, {
          normal: { url: makeSvgMarker('#888', style.glyph, 28), scaledSize: new g.Size(28, 36), anchor: new g.Point(14, 36) },
          highlight: { url: makeHighlightMarker(primaryColor, style.glyph, 40), scaledSize: new g.Size(40, 54), anchor: new g.Point(20, 54) },
        });
      }

      const icons = iconCache.get(cat)!;

      // Markers are always created with the "normal" (unselected)
      // icon. Selection visuals (highlight icon, bounce, pan, zoom)
      // are applied by the separate `selectedPoiIndex` effect below
      // — keeping selection out of this function means selecting a
      // POI no longer tears down and rebuilds every marker, and no
      // longer re-runs `fitBounds` (which was pulling the camera
      // back out to enclose every POI on the map each time the user
      // picked a single one).
      const marker = new g.Marker({
        position: pos, map,
        title: poi.poi_name || '',
        icon: icons.normal,
        zIndex: 1,
      });

      marker.addListener('click', () => onMarkerClick(origIndex));

      markersRef.current.push({ marker, normalIcon: icons.normal, highlightIcon: icons.highlight, poiIndex: origIndex });
    }

    // Only fit the camera the first time we see a non-empty POI set,
    // and re-fit when the POI count truly changes. This makes
    // fitBounds idempotent against parent re-renders — selecting a
    // POI churns `pois` and `onMarkerClick` reference identity and
    // therefore re-fires this effect, but with the same content,
    // which would otherwise cause a visible zoom-out / zoom-in
    // flicker every click.
    const visibleCount = markersRef.current.length;
    if (hasPoiBounds && lastFitPoiCountRef.current !== visibleCount) {
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      lastFitPoiCountRef.current = visibleCount;
      const listener = g.event.addListener(map, 'idle', () => {
        if (map.getZoom() > 15) map.setZoom(15);
        g.event.removeListener(listener);
      });
    }
  }, [pois, activeTab, propertyLat, propertyLng, primaryHue, primaryColor, onMarkerClick]);

  useEffect(() => { if (ready) syncMarkers(); }, [ready, syncMarkers]);

  // When selected POI changes, update marker icons, pan to the
  // selection, and zoom in to street level so the user can see the
  // immediate surroundings of the chosen POI. We only zoom in when
  // the current zoom is below the target — if the user has manually
  // zoomed in further than the target (e.g. street-level walking
  // around), selecting a different POI shouldn't yank them back out.
  const SELECTED_POI_ZOOM = 17;
  useEffect(() => {
    const g = (window as any).google?.maps;
    const map = mapRef.current;
    if (!g || !map || !ready) return;

    for (const entry of markersRef.current) {
      const isSelected = entry.poiIndex === selectedPoiIndex;
      entry.marker.setIcon(isSelected ? entry.highlightIcon : entry.normalIcon);
      entry.marker.setZIndex(isSelected ? 900 : 1);
      if (isSelected) {
        entry.marker.setAnimation(g.Animation.BOUNCE);
        setTimeout(() => entry.marker.setAnimation(null), 1400);
        map.panTo(entry.marker.getPosition());
        const currentZoom = map.getZoom() ?? 0;
        if (currentZoom < SELECTED_POI_ZOOM) {
          map.setZoom(SELECTED_POI_ZOOM);
        }
      }
    }
  }, [selectedPoiIndex, ready]);

  return (
    <div className="w-full h-full relative rounded overflow-hidden">
      <div ref={containerRef} className="w-full h-full" />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#f5f5f5]">
          <span className="font-body text-sm text-[var(--vibe-color-text-muted)] animate-pulse">Loading map…</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function NeighborhoodMap() {
  const property = useProperty();
  const pois = useCollectionBinding<POI>('neighborhoodmap.pois', { collection: 'neighborhood_pois' });

  const propLat = usePropertyField('property_address_latitude');
  const propLng = usePropertyField('property_address_longitude');
  const propertyIcon = (property?.property_icon as string) || '';
  const primaryColor = (property?.property_primary_color as string) || '#0a1f2e';
  // Right-column surface (title + accordion + the desktop gap between
  // the map and the list) sits one tier lighter than the surrounding
  // primary-color section so the whole list panel reads as a distinct
  // band, not just a stack of header bars blending into the section.
  // +5 lightness keeps the same hue/saturation and is intentionally
  // subtle — earlier iterations at +18 and +8 both read as too pale
  // against the brand primary. At +5 the band is just barely
  // distinguishable, which is what the editorial reference calls for.
  // The accordion sections inside reuse this same variable so they
  // merge seamlessly with the panel surface and the accordion
  // structure is communicated by hairline dividers instead of a
  // contrasting fill.
  const accordionBg = useMemo(() => lighten(primaryColor, 5), [primaryColor]);

  const allPois = pois && pois.length > 0 ? pois : [];

  const categories = useMemo(() => {
    const cats = new Set<string>();
    for (const poi of allPois) cats.add(poi.poi_type || 'Nearby');
    return Array.from(cats);
  }, [allPois]);

  const [selectedPoiIndex, setSelectedPoiIndex] = useState<number | null>(null);
  // Which category accordion panels are open. Defaults to the first
  // category being open so editors always see content on first paint.
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    () => (categories.length > 0 ? new Set([categories[0]]) : new Set()),
  );
  const listRef = useRef<HTMLDivElement>(null);

  // POIs grouped by category, preserving original list indices so map
  // markers and the selection state still resolve back to a stable
  // `origIndex` against `allPois`.
  const groupedPois = useMemo(() => {
    return categories.map((cat) => ({
      category: cat,
      items: allPois
        .map((poi, origIndex) => ({ poi, origIndex }))
        .filter(({ poi }) => (poi.poi_type || 'Nearby') === cat),
    }));
  }, [allPois, categories]);

  const toggleCategory = useCallback((cat: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  }, []);

  // Clicking a row toggles its map-marker highlight. There is no
  // per-POI detail panel anymore — rows just show name + walk distance.
  const handleListItemClick = useCallback((origIndex: number) => {
    setSelectedPoiIndex((prev) => (prev === origIndex ? null : origIndex));
  }, []);

  // Read `allPois` through a ref so `handleMarkerClick` can keep
  // empty deps and never get a new identity across renders. This
  // matters because the callback is passed as `onMarkerClick` into
  // `GoogleMap`; if it changed every render, `syncMarkers` inside
  // `GoogleMap` would recreate, every marker on the map would be
  // torn down and rebuilt on every click, and the camera would jump.
  const allPoisRef = useRef(allPois);
  useEffect(() => { allPoisRef.current = allPois; }, [allPois]);

  // Clicking a map marker: select on the list and expand the POI's
  // category section if it's collapsed, then scroll the list to the
  // matching row so the user can read its name + walk distance.
  const handleMarkerClick = useCallback((origIndex: number) => {
    setSelectedPoiIndex(origIndex);
    const poi = allPoisRef.current[origIndex];
    const cat = poi?.poi_type || 'Nearby';
    setExpandedCategories((prev) => {
      if (prev.has(cat)) return prev;
      const next = new Set(prev);
      next.add(cat);
      return next;
    });
    // Wait one tick so the freshly-expanded section is mounted before
    // we try to scroll its child row into view.
    setTimeout(() => {
      const el = listRef.current?.querySelector(`[data-poi-index="${origIndex}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
  }, []);

  const lat = parseFloat(propLat as string || '');
  const lng = parseFloat(propLng as string || '');
  const hasCoords = !isNaN(lat) && !isNaN(lng);

  return (
    <section
      className="py-8 md:py-12"
      style={{
        background: 'var(--vibe-color-primary)',
        // Expose the lightened primary as a CSS variable so the
        // accordion's scoped styles below can reference it without
        // having to inline the computed color on every element.
        ['--nb-accordion-bg' as any]: accordionBg,
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="nb-layout">
          {/* Map on the LEFT, taking the larger of the two columns. The
              map shows every POI regardless of which category panels
              are open — accordion panels are a list-side affordance, not
              a marker filter. */}
          <div className="nb-map-container">
            {hasCoords ? (
              <GoogleMap
                propertyLat={lat} propertyLng={lng}
                propertyIcon={propertyIcon} primaryColor={primaryColor}
                pois={allPois} activeTab="all"
                selectedPoiIndex={selectedPoiIndex}
                onMarkerClick={handleMarkerClick}
              />
            ) : (
              <div className="w-full h-full bg-[#f5f5f5] flex items-center justify-center text-[var(--vibe-color-text-muted)]">
                <p className="font-body">Map coordinates not available</p>
              </div>
            )}
          </div>

          {/* List on the RIGHT, rendered as a stack of collapsible
              category sections. Each section's header bar is painted in
              the theme primary color with text-on-primary text — the old
              horizontal tab bar's role is absorbed into these section
              headers. Clicking the header expands/collapses the section.

              The "Explore the Neighborhoods" title sits at the top of
              THIS column (not above the whole layout) so on desktop it
              never extends higher than the map to its left. On mobile
              (single column) it appears between the map and the
              accordion, which is the natural reading order. */}
          <div className="nb-list-container">
            <h2 className="nb-list-title">Explore the Neighborhoods</h2>
            <div ref={listRef} className="nb-poi-list">
              {groupedPois.length > 0 ? (
                groupedPois.map(({ category, items }) => {
                  const isOpen = expandedCategories.has(category);
                  const panelId = `nb-section-${category.replace(/\s+/g, '-').toLowerCase()}`;
                  return (
                    <div key={category} className={`nb-section${isOpen ? ' nb-section--open' : ''}`}>
                      <button
                        type="button"
                        className="nb-section-header"
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() => toggleCategory(category)}
                      >
                        <span className="nb-section-label">
                          <CategoryIcon poiType={category} />
                          <span className="nb-section-title">{category}</span>
                        </span>
                        <span className="nb-section-meta">
                          {/* Plus / minus toggle icon. The horizontal
                              bar is always drawn; the vertical bar is
                              hidden via CSS when the section is open,
                              so the icon morphs from "+" (closed) to
                              "−" (open) with a quick fade. The POI
                              count chip used to live next to this
                              toggle but was removed so the header bar
                              matches the via6living.com reference
                              (icon + label on the left, toggle alone
                              on the right). */}
                          <span
                            className={`nb-section-icon${isOpen ? ' nb-section-icon--open' : ''}`}
                            aria-hidden="true"
                          >
                            <span className="nb-section-icon-h" />
                            <span className="nb-section-icon-v" />
                          </span>
                        </span>
                      </button>

                      {isOpen && (
                        <div id={panelId} className="nb-section-body" role="region">
                          {items.map(({ poi, origIndex }) => {
                            // Only walking time is shown per the spec —
                            // omit the line entirely if the field is
                            // empty so rows stay clean.
                            const walk = hasVal(poi.poi_walking_minutes)
                              ? formatMinutes(poi.poi_walking_minutes, 'walk')
                              : null;
                            const isActive = selectedPoiIndex === origIndex;
                            return (
                              <div
                                key={origIndex}
                                data-poi-index={origIndex}
                                className={`nb-poi-row${isActive ? ' nb-poi-row--active' : ''}`}
                                onClick={() => handleListItemClick(origIndex)}
                              >
                                <span className="nb-poi-name">{poi.poi_name}</span>
                                {walk && <span className="nb-poi-dist">{walk}</span>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div
                  className="text-center py-12"
                  style={{ color: 'var(--vibe-color-text-on-primary, #fff)', opacity: 0.7 }}
                >
                  <p className="font-body text-lg">No points of interest found.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* ===== Layout =====
           Desktop: MAP on the LEFT (2fr) and LIST on the RIGHT (1fr) —
           map is rendered first in JSX, so source order already gives us
           the correct visual order on both desktop and mobile. Mobile
           (<1024px) collapses to a single column with the map on top and
           the accordion list flowing underneath. */
        .nb-layout {
          display: grid;
          grid-template-columns: 1fr;
          /* Vertical stack on mobile keeps a real gap so the map and
             the list panel don't visually merge when stacked. */
          gap: 2rem;
          max-width: 100%;
          overflow: hidden;
        }
        @media (min-width: 1024px) {
          /* Desktop: no horizontal grid gap. The list container paints
             its own lighter-primary background and uses left padding
             to create the visual separation from the map, so the
             "gap" between the two columns is now part of the lighter
             band (matching the user's request that the lighter
             surface encompass the space between accordion and map).
             Column ratio is 3:2 (~60/40) — the map is still the
             primary visual but the accordion now gets enough width
             for category titles and POI names to breathe without
             ellipsing. minmax floor of 340px keeps the accordion
             usable on narrow 1024px-class viewports. */
          .nb-layout { grid-template-columns: 3fr minmax(340px, 2fr); gap: 0; }
        }
        .nb-map-container {
          width: 100%;
          min-width: 0;
          aspect-ratio: 4/3;
          position: relative;
          overflow: hidden;
        }
        @media (min-width: 1024px) {
          .nb-map-container { aspect-ratio: auto; min-height: 560px; }
        }
        .nb-list-container {
          display: flex;
          flex-direction: column;
          min-width: 0;
          max-height: 60vh;
          /* Lighter-primary band that encompasses the title above the
             accordion AND (on desktop, where grid gap is 0) the visual
             space between the map and the accordion. Padding does
             double duty: it insets the title/accordion away from the
             panel edges and replaces the removed grid gap on the left
             so the band reads as continuous from the map's right edge
             through to the page's right margin. */
          background: var(--nb-accordion-bg, var(--vibe-color-primary));
          padding: 1.25rem 1rem;
        }
        @media (min-width: 1024px) {
          .nb-list-container {
            max-height: 640px;
            min-height: 560px;
            /* Extra left padding on desktop matches what used to be
               the 2.5rem grid gap, so the inner title/accordion still
               sit visually separated from the map by the same gutter
               — now filled by the lighter band rather than the page
               background. */
            padding: 1.5rem 1.25rem 1.5rem 2.5rem;
          }
        }

        /* ===== Accordion title =====
           Lives inside .nb-list-container so it occupies the right
           column on desktop — that's what keeps it from sitting higher
           than the map. flex-shrink:0 ensures it doesn't compete with
           .nb-poi-list (which has flex: 1 1 auto) for vertical space. */
        .nb-list-title {
          flex: 0 0 auto;
          /* Generous bottom margin so the title sits as a clear
             standalone label above the accordion stack rather than
             feeling tacked onto the first header bar. */
          margin: 0 0 1.5rem 0;
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          line-height: 1.2;
          font-weight: 600;
          letter-spacing: 0.01em;
          color: var(--vibe-color-text-on-primary, #fff);
        }
        @media (min-width: 1024px) {
          .nb-list-title {
            font-size: 1.25rem;
            margin-bottom: 1.75rem;
          }
        }

        /* ===== Accordion category sections =====
           Header bar is painted in the theme primary color with text on
           primary (white in this theme). Clicking the header expands
           the section.

           Section headers use position:sticky so they remain pinned
           to the top of the scrollable list area while their body is in
           view — opening one section therefore never pushes another
           section's header out of sight. The list itself is scrollable
           with a visible side scrollbar (see scrollbar rules below). */
        .nb-poi-list {
          flex: 1 1 auto;
          overflow-y: auto;
          padding-right: 6px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          /* Firefox scrollbar — visible thumb on a subtle track. */
          scrollbar-width: thin;
          scrollbar-color: rgba(255,255,255,0.45) rgba(255,255,255,0.12);
        }
        /* WebKit scrollbar — wider than the prior 4px hairline so it
           actually reads as a scroll affordance on the right edge of
           the list. Track and thumb pick up white-on-primary so they
           contrast against the navy outer background. */
        .nb-poi-list::-webkit-scrollbar { width: 8px; }
        .nb-poi-list::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.10);
          border-radius: 8px;
        }
        .nb-poi-list::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.45);
          border-radius: 8px;
        }
        .nb-poi-list::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.65); }

        .nb-section {
          border-radius: 4px;
          /* No overflow:hidden — that would clip the sticky header at
             the section's bounds and break the sticky pin behavior.
             Rounded corners are achieved by matching radii on the
             header (top) and the last body row (bottom) instead. */
          animation: nb-fade-in 0.3s ease both;
        }

        .nb-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 0.9rem 1rem;
          /* Lighter shade of the brand primary (computed via the
             lighten() rule at component scope) so the accordion lifts
             visually off the surrounding primary-color section. Falls
             back to the raw primary if the variable somehow fails to
             resolve, which keeps the white-on-dark text legible. */
          background: var(--nb-accordion-bg, var(--vibe-color-primary));
          color: var(--vibe-color-text-on-primary, #fff);
          border: none;
          /* Editorial Via6-style divider beneath every header bar.
             Spans the full width of the bar — title side + count/+
             side both sit on top of the same divider line, so closed
             sections show as a stack of titles with dividers between
             them and an open section gets a divider between its
             header and the first POI row inside the body. Higher
             opacity (0.6) gives the line a near-white presence so the
             accordion structure reads clearly against the lighter
             primary band. */
          border-bottom: 1px solid rgba(255, 255, 255, 0.6);
          cursor: pointer;
          text-align: left;
          font-family: var(--vibe-font-body);
          transition: filter 0.15s;
          /* Sticky pin: as the user scrolls within .nb-poi-list, the
             active section's header stays clipped to the top of the
             scroll viewport until the next section starts pushing it
             out. This way all section headers remain reachable even
             when one section is open and tall. */
          position: sticky;
          top: 0;
          z-index: 2;
          border-radius: 4px 4px 0 0;
        }
        .nb-section:not(.nb-section--open) .nb-section-header {
          border-radius: 4px;
        }
        .nb-section-header:hover { filter: brightness(1.1); }
        .nb-section-header:focus-visible {
          outline: 2px solid var(--vibe-color-text-on-primary, #fff);
          outline-offset: -3px;
        }
        /* Left side of the header bar: category glyph + title text. */
        .nb-section-label {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          min-width: 0;
        }
        .nb-section-cat-icon {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
          display: block;
          color: var(--vibe-color-text-on-primary, #fff);
        }
        .nb-section-title {
          font-family: var(--vibe-font-body);
          font-size: 1.15rem;
          font-weight: 500;
          letter-spacing: 0.01em;
        }
        .nb-section-meta {
          display: inline-flex;
          align-items: center;
          gap: 0.65rem;
        }

        /* Plus / minus toggle. Two thin bars in a 16x16 box: a
           horizontal bar that is always drawn and a vertical bar that
           fades + scales out when the section is open. */
        .nb-section-icon {
          position: relative;
          display: inline-block;
          width: 14px;
          height: 14px;
          flex-shrink: 0;
        }
        .nb-section-icon-h,
        .nb-section-icon-v {
          position: absolute;
          background: currentColor;
          border-radius: 1px;
          transition: transform 0.2s ease, opacity 0.2s ease;
        }
        .nb-section-icon-h {
          left: 0; right: 0; top: 50%;
          height: 2px;
          transform: translateY(-50%);
        }
        .nb-section-icon-v {
          top: 0; bottom: 0; left: 50%;
          width: 2px;
          transform: translateX(-50%);
          opacity: 1;
        }
        .nb-section-icon--open .nb-section-icon-v {
          opacity: 0;
          transform: translateX(-50%) scaleY(0);
        }

        /* ===== Expanded section body =====
           Painted in the theme primary color so the entire section
           reads as one continuous primary-on-primary block. Inner POI
           rows use text-on-primary (white) text. Dividers between rows
           use a low-alpha white so they're visible but quiet. */
        .nb-section-body {
          /* Matches the header bar so an open section reads as one
             continuous lighter-primary surface from header through
             body. */
          background: var(--nb-accordion-bg, var(--vibe-color-primary));
          color: var(--vibe-color-text-on-primary, #fff);
          padding: 0 1rem;
          border-radius: 0 0 4px 4px;
          animation: nb-slide-down 0.25s ease;
        }

        /* ===== POI row inside an expanded section =====
           Minimal: just the POI name and walking time, no chevron, no
           detail expansion. Clicking the row still selects the matching
           map marker so users can navigate between list and map.

           Rows are inset 1rem from the section-body's left edge so the
           expanded items read as visually subordinate to the section
           header above them (header glyph + label at the body's left
           edge, items indented underneath). Hover / active extend the
           background to the right while preserving the inset on the
           left. */
        .nb-poi-row {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 0.75rem;
          /* No per-row divider — the section header's white underline
             already communicates structural separation. Generous
             vertical padding (0.85rem top/bottom) gives the rows
             room to breathe so they don't crowd together inside the
             expanded section body. */
          padding: 0.85rem 0 0.85rem 1rem;
          cursor: pointer;
          transition: background 0.15s, padding 0.15s;
          color: var(--vibe-color-text-on-primary, #fff);
        }
        .nb-poi-row:hover {
          background: rgba(255, 255, 255, 0.06);
          padding-right: 0.5rem;
          margin-right: -0.5rem;
        }
        .nb-poi-row--active {
          background: rgba(255, 255, 255, 0.10);
          padding-right: 0.5rem;
          margin-right: -0.5rem;
        }

        .nb-poi-name {
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 400;
          line-height: 1.3;
          color: var(--vibe-color-text-on-primary, #fff);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
          flex: 1 1 auto;
        }
        .nb-poi-dist {
          font-family: var(--vibe-font-body);
          font-size: 0.68rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          color: var(--vibe-color-text-on-primary, #fff);
          opacity: 0.8;
          white-space: nowrap;
          flex-shrink: 0;
        }
        /* ===== Animations ===== */
        @keyframes nb-fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes nb-slide-down {
          from { opacity: 0; max-height: 0; }
          to { opacity: 1; max-height: 600px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .nb-section, .nb-section-body { animation: none; }
        }

        /* ===== Google Maps info window cleanup ===== */
        .gm-style .gm-style-iw-c { border-radius: 6px !important; box-shadow: 0 4px 20px rgba(0,0,0,0.12) !important; padding: 8px 12px !important; }
        .gm-style .gm-style-iw-d { overflow: hidden !important; }
        .gm-style .gm-style-iw-tc::after { background: white !important; }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodMap',
  displayName: 'Neighborhood Map',
  tokens: {},
  collectionBindings: {
    'neighborhoodmap.pois': {
      label: 'Points of Interest',
      collection: 'neighborhood_pois',
    },
  },
};

(NeighborhoodMap as any).contentSchema = contentSchema;

export default NeighborhoodMap;
